/**
 * Pool de promesses concurrent borné — primitive d'orchestration pour
 * `MockupTemplateBulkUploadModal`. Colocalisée intentionnellement (pas
 * dans `utils/`) : un seul appelant aujourd'hui, on promouvra le jour
 * où un 2e use-case existera vraiment (cf plan refonte upload mockup).
 *
 * Pourquoi un fichier dédié plutôt qu'inline dans le composant : les
 * tests vitest doivent pouvoir charger la fonction sans tirer tout
 * React + axios + tanstack-query (qui ralentissent vitest à plusieurs
 * minutes par run pour un test de logique pure ~5 ms).
 *
 * Le scheduler teste `shouldContinue()` AVANT de pull le prochain item :
 * sans ça, fermer la modal pendant l'upload continuerait à drainer la
 * queue et enverrait N requêtes inutiles côté serveur. Les workers
 * déjà en cours finissent — idempotent côté BE (UNIQUE sha256/slug).
 */

export async function _runWithConcurrency<T>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number) => Promise<unknown>,
  shouldContinue: () => boolean = () => true,
): Promise<void> {
  if (items.length === 0) return
  if (limit <= 0) {
    throw new Error(`_runWithConcurrency: limit must be > 0, got ${limit}`)
  }

  let cursor = 0
  const total = items.length
  const effectiveLimit = Math.min(limit, total)

  const run = async (): Promise<void> => {
    while (shouldContinue()) {
      const idx = cursor++
      if (idx >= total) return
      try {
        await worker(items[idx], idx)
      } catch {
        // Le worker est responsable de gérer ses propres erreurs (les
        // matérialiser dans son state). On ne propage pas — un fichier
        // raté ne doit pas annuler les autres.
      }
    }
  }

  await Promise.all(Array.from({ length: effectiveLimit }, () => run()))
}
